<template>
    <div class="flex-1 flex flex-col overflow-hidden min-h-0">
        <div class="shrink-0 bg-white border-b border-gray-100 px-6 py-4">
            <div class="max-w-3xl mx-auto">
                <BasePageTitle no-border title="Senior Leader Self-Assessment" subtitle="Take a few minutes to reflect on your leadership behaviors across 5 key competencies." />
            </div>
        </div>

        <div class="flex-1 overflow-y-auto">
            <Transition name="fade" mode="out-in">
                <div v-if="phase === 'disclaimer'" key="disclaimer" class="max-w-3xl mx-auto px-6 py-10">
                    <div class="bg-white rounded-2xl border border-gray-100 shadow-sm p-8">
                        <div class="flex items-center gap-3 mb-5">
                            <div class="w-10 h-10 rounded-xl bg-valence-purple/10 flex items-center justify-center shrink-0">
                                <i class="bi bi-shield-check text-valence-purple text-lg" aria-hidden="true"></i>
                            </div>
                            <!-- FIXME: Consider extracting assessment section headers to a shared BasePageHeader component -->
                            <h2 class="title-body-bold text-gray-900">Assessment Disclaimer</h2>
                        </div>
                        <p class="body text-gray-700 mb-4">
                            The CVS Health Leadership Competencies set a consistent, elevated standard for how we lead and develop leadership capability across our company. Our
                            five leadership competencies are People-First Leadership, Drives Performance, Agile Learner, Champions AI, and Shapes the Future. This self-assessment
                            is designed to help you identify areas of strength and opportunities to grow.
                        </p>
                        <p class="body text-gray-700 mb-4">
                            The goal of this self-assessment is to promote development. This is <strong>not</strong> intended to be a rating or evaluation of your current
                            performance in your role and is separate from the performance management process. Individual and aggregate data will be available to only a limited
                            group of HR colleagues to inform future assessments and development opportunities related to the competencies.
                        </p>
                        <p class="body text-gray-700 mb-4">
                            Please respond as candidly as you can based on your professional experiences. Your reflections will help you to identify strengths to build on and to
                            identify some focused growth areas.
                        </p>
                        <p class="body text-gray-700">If you are unsure on an item, please choose the best response based on your typical behavior.</p>
                    </div>
                </div>

                <div v-else-if="phase === 'instructions'" key="instructions" class="max-w-3xl mx-auto px-6 py-10">
                    <div class="bg-white rounded-2xl border border-gray-100 shadow-sm p-8">
                        <div class="flex items-center gap-3 mb-5">
                            <div class="w-10 h-10 rounded-xl bg-valence-purple/10 flex items-center justify-center shrink-0">
                                <i class="bi bi-list-check text-valence-purple text-lg" aria-hidden="true"></i>
                            </div>
                            <h2 class="title-body-bold text-gray-900">How This Works</h2>
                        </div>
                        <p class="body text-gray-700 mb-4">
                            This assessment contains <strong>20 different behaviors</strong> and definitions that you will self-assess. For each question, please consider how this
                            behavior appears — or does not presently appear — in your daily work.
                        </p>
                        <p class="body text-gray-700 mb-4">
                            There is a rating scale from <strong>1–5</strong> for each behavior. You may also mark a behavior as <strong>N/A</strong> if it does not apply to your
                            role.
                        </p>
                        <p class="body text-gray-700 mb-4">
                            Please remember that this is <strong>not a performance evaluation</strong>, but rather a tool to assist in your ongoing development.
                        </p>
                        <p class="body text-gray-700 mb-4">
                            An example competency, behavior, definition, and rating scale will be presented to you next so that you can orient yourself before beginning the
                            assessment.
                        </p>
                        <p class="body text-gray-500">
                            Once you complete the assessment, you will be given a choice to download your summary or to start coaching with Nadia to take your self-reflection
                            further.
                        </p>
                    </div>
                </div>

                <div v-else-if="phase === 'example'" key="example" class="max-w-3xl mx-auto px-6 py-10 space-y-5">
                    <div class="bg-white rounded-2xl border border-gray-100 shadow-sm p-8">
                        <div class="flex items-center gap-3 mb-5">
                            <div class="w-10 h-10 rounded-xl bg-valence-purple/10 flex items-center justify-center shrink-0">
                                <i class="bi bi-eye text-valence-purple text-lg" aria-hidden="true"></i>
                            </div>
                            <h2 class="title-body-bold text-gray-900">Example Question</h2>
                        </div>
                        <p class="body-small text-gray-500 mb-6">
                            You will be presented a series of behaviors and statements on which you will be asked to assess yourself. Here is what a question looks like:
                        </p>
                        <div class="bg-gray-50 rounded-xl border border-gray-100 p-5 mb-6">
                            <div class="flex items-start justify-between gap-4 mb-3">
                                <div class="flex-1">
                                    <!-- One-off: 12px font-bold tracking-widest eyebrow; no exact utility match -->
                                    <p class="details font-bold tracking-widest text-valence-purple uppercase mb-1">People-First Leadership</p>
                                    <h3 class="body-bold text-gray-900">Sets performance expectations</h3>
                                    <p class="body-small text-gray-400 mt-1">
                                        Communicates high performance expectations linked to business goals; checks for understanding of and commitment to goals.
                                    </p>
                                </div>
                                <div class="shrink-0 flex flex-col items-center justify-center w-16 h-16 rounded-xl bg-gray-200 text-gray-400">
                                    <span class="title-body-bold leading-none">N/A</span>
                                </div>
                            </div>
                            <div class="mt-3">
                                <div class="h-1 rounded-full bg-gray-200 w-full mb-2"></div>
                                <div class="flex justify-between px-[10px]">
                                    <span class="text-[10px] text-gray-300 font-semibold">N/A</span>
                                    <span v-for="n in 5" :key="n" class="text-[10px] text-gray-300 font-semibold">{{ n }} · {{ rating_labels[String(n)] }}</span>
                                </div>
                            </div>
                        </div>
                        <p class="body-small-bold text-gray-700 mb-3">Rating scale (applies to all questions):</p>
                        <div class="divide-y divide-gray-50">
                            <div class="flex gap-3 py-2.5">
                                <div class="shrink-0 w-7 h-7 rounded-full bg-gray-100 text-gray-500 details flex items-center justify-center">N/A</div>
                                <div>
                                    <span class="body-small-bold text-gray-900">Behavior not yet applicable</span>
                                    <span class="body-small text-gray-400"> — This behavior doesn't currently apply to my role.</span>
                                </div>
                            </div>
                            <div v-for="n in 5" :key="n" class="flex gap-3 py-2.5">
                                <div class="shrink-0 w-7 h-7 rounded-full bg-valence-purple/10 text-valence-purple details flex items-center justify-center">
                                    {{ n }}
                                </div>
                                <div>
                                    <span class="body-small-bold text-gray-900">{{ rating_labels[String(n)] }}</span>
                                    <span class="body-small text-gray-400"> — {{ rating_descriptions[String(n)] }}</span>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </Transition>
        </div>

        <div class="shrink-0 bg-white border-t border-gray-100 px-6 py-4">
            <div class="max-w-3xl mx-auto flex items-center justify-between">
                <button
                    v-if="phase !== 'disclaimer'"
                    class="px-5 py-2.5 rounded-xl border border-gray-200 text-gray-600 body-bold hover:bg-gray-50 transition-colors"
                    :disabled="isStartingAssessment"
                    @click="emit('intro-prev')">
                    ← Back
                </button>
                <div v-else></div>
                <button
                    class="px-6 py-2.5 rounded-xl body-bold bg-valence-purple text-white hover:bg-valence-purple/90 transition-colors"
                    :disabled="isStartingAssessment"
                    @click="emit('intro-next')">
                    {{ phase === "example" ? (isStartingAssessment ? "Starting..." : "Start Assessment →") : "Continue →" }}
                </button>
            </div>
            <div v-if="startError" class="max-w-3xl mx-auto mt-2 details text-amber-600">
                {{ startError }}
            </div>
        </div>
    </div>
</template>

<script setup lang="ts">
import BasePageTitle from "~vue/components/BasePageTitle.vue";

type IntroPhase = "disclaimer" | "instructions" | "example";

interface Props {
    phase: IntroPhase;
    isStartingAssessment: boolean;
    startError: string | null;
    rating_labels: Record<string, string>;
    rating_descriptions: Record<string, string>;
}

defineProps<Props>();

const emit = defineEmits<{
    (e: "intro-prev"): void;
    (e: "intro-next"): void;
}>();
</script>
